import React, { FC } from "react";
import { LegalBlock } from "data/legalTerms";

export interface LegalDocumentProps {
  blocks: LegalBlock[];
}

const POPPINS = { fontFamily: "Poppins, sans-serif" } as const;
const ROBOTO = { fontFamily: "Roboto, sans-serif" } as const;

// Turns any http(s) URL inside a text run into a clickable link (new tab).
// Trailing punctuation (e.g. a comma or closing paren right after the URL) is
// kept outside the link so it isn't swallowed into the href.
const URL_RE = /(https?:\/\/[^\s]+)/g;
const linkify = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  URL_RE.lastIndex = 0;
  while ((match = URL_RE.exec(text)) !== null) {
    const start = match.index;
    let url = match[0];
    let trailing = "";
    const trailingMatch = url.match(/[.,;:!?)\]]+$/);
    if (trailingMatch) {
      trailing = trailingMatch[0];
      url = url.slice(0, url.length - trailing.length);
    }
    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));
    nodes.push(
      <a
        key={start}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#B88333] underline underline-offset-2 break-words hover:opacity-80"
      >
        {url}
      </a>
    );
    if (trailing) nodes.push(trailing);
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
};

// Renders **double-asterisk** spans as bold and auto-links URLs; everything
// else is plain text.
const renderInline = (text: string): React.ReactNode =>
  text.split("**").map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-[#01152E]">
        {linkify(part)}
      </strong>
    ) : (
      <React.Fragment key={i}>{linkify(part)}</React.Fragment>
    )
  );

// Presentational renderer for the standalone legal documents. It mirrors the
// design of the terms pages: a centered white column, justified body copy,
// numbered section headings and bulleted rules with bold lead-in labels.
const LegalDocument: FC<LegalDocumentProps> = ({ blocks }) => {
  // The page title (h1) renders as a full-width dark band that mirrors the
  // wishlist/legal page header; the rest of the document sits in a centered
  // reading column below it.
  const title = blocks.find(
    (b): b is Extract<LegalBlock, { type: "h1" }> => b.type === "h1"
  );
  const bodyBlocks = blocks.filter((b) => b.type !== "h1");

  return (
    <div className="w-full bg-white">
      {title && (
        <div
          className="w-full flex items-center min-h-[72px] sm:min-h-[90px] lg:min-h-[110px] py-4 px-4 sm:px-8 lg:px-[120px]"
          style={{ backgroundColor: "#01152E" }}
        >
          <h1
            className="text-[22px] sm:text-[28px] lg:text-[32px] leading-[28px] sm:leading-[36px] lg:leading-[40px]"
            style={{ ...POPPINS, fontWeight: 600, color: "#ffffff", margin: 0 }}
          >
            {title.text}
          </h1>
        </div>
      )}
      {/* Same horizontal gutter as the heading band so the body's left edge
          lines up with "Residences Terms of Use"; max-width keeps line length
          readable, left-aligned (not centered). */}
      <div className="px-4 sm:px-8 lg:px-[120px] py-10 sm:py-12">
        <div className="max-w-[1040px]">
          {bodyBlocks.map((block, i) => {
          switch (block.type) {
            case "h2":
              return (
                <h2
                  key={i}
                  className="text-[17px] sm:text-[19px] leading-snug font-semibold text-[#01152E] mt-8 mb-3 break-words"
                  style={POPPINS}
                >
                  {renderInline(block.text)}
                </h2>
              );
            case "h3":
              return (
                <h3
                  key={i}
                  className="text-[14px] font-semibold text-[#01152E] mt-6 mb-2 break-words"
                  style={POPPINS}
                >
                  {renderInline(block.text)}
                </h3>
              );
            case "p":
              return (
                <p
                  key={i}
                  className="text-[14px] leading-[24px] text-[#445162] text-justify mb-4"
                  style={ROBOTO}
                >
                  {renderInline(block.text)}
                </p>
              );
            case "ul":
              return (
                <ul
                  key={i}
                  className="list-disc pl-6 mb-4 space-y-1.5"
                  style={ROBOTO}
                >
                  {block.items.map((item, j) => (
                    <li
                      key={j}
                      className="text-[14px] leading-[24px] text-[#445162] text-justify pl-1"
                    >
                      {renderInline(item)}
                    </li>
                  ))}
                </ul>
              );
            default:
              return null;
          }
          })}
        </div>
      </div>
    </div>
  );
};

export default LegalDocument;
